/**
 * KAN-835: Web Push устройства менеджера (без React): поддержка браузером,
 * подписка через service worker (public/sw.js) и тело запроса для backend.
 *
 * iPhone и iPad дают Web Push только приложению, добавленному на экран Домой
 * (iOS 16.4+), поэтому в Safari без установки ответ "ios-install".
 */
import { isIosDevice, isStandaloneDisplay } from '@/lib/pwaInstall';
import { registerServiceWorker } from '@/lib/serviceWorker';
import type { PushSubscriptionBody } from '@/services/api/managerNotifications';

export type PushSupport = 'supported' | 'ios-install' | 'unsupported';

export interface PushEnv {
  win: Window;
  nav: Navigator;
}

export function defaultPushEnv(): PushEnv {
  return { win: window, nav: navigator };
}

/** VAPID-ключ base64url -> байты для applicationServerKey. */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export function pushSupport(env: PushEnv): PushSupport {
  const { win, nav } = env;
  if (isIosDevice(nav.userAgent || '', nav.maxTouchPoints || 0) && !isStandaloneDisplay(win)) {
    return 'ios-install';
  }
  const hasWorker = 'serviceWorker' in nav && !!nav.serviceWorker;
  if (!hasWorker || !('PushManager' in win) || !('Notification' in win)) return 'unsupported';
  return 'supported';
}

/** Разрешение на уведомления: 'default' | 'granted' | 'denied'. */
export function notificationPermission(env: PushEnv): NotificationPermission {
  const notification = (env.win as Window & { Notification?: typeof Notification }).Notification;
  return notification ? notification.permission : 'denied';
}

/** Спросить разрешение. Вызывать только по нажатию пользователя. */
export async function requestNotificationPermission(env: PushEnv): Promise<NotificationPermission> {
  const notification = (env.win as Window & { Notification?: typeof Notification }).Notification;
  if (!notification) return 'denied';
  return notification.requestPermission();
}

async function registration(env: PushEnv): Promise<ServiceWorkerRegistration | undefined> {
  const existing = await env.nav.serviceWorker.getRegistration('/');
  if (existing) return existing;
  // В dev-сборке main.tsx service worker не регистрирует: регистрируем по требованию.
  if (!(await registerServiceWorker(env.nav))) return undefined;
  return env.nav.serviceWorker.ready;
}

/** Текущая подписка этого устройства или null. */
export async function deviceSubscription(env: PushEnv): Promise<PushSubscription | null> {
  if (pushSupport(env) !== 'supported') return null;
  const reg = await env.nav.serviceWorker.getRegistration('/');
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/** Подписать устройство ключом VAPID (прежняя подписка с другим ключом заменяется). */
export async function subscribeDevice(env: PushEnv, publicKey: string): Promise<PushSubscription> {
  const reg = await registration(env);
  if (!reg) throw new Error('Не удалось запустить фоновую службу приложения');
  const options = {
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  };
  try {
    return await reg.pushManager.subscribe(options);
  } catch (error) {
    const existing = await reg.pushManager.getSubscription();
    if (!existing) throw error;
    await existing.unsubscribe();
    return reg.pushManager.subscribe(options);
  }
}

export function subscriptionBody(subscription: PushSubscription): PushSubscriptionBody {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint || subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh || '', auth: json.keys?.auth || '' },
  };
}
